import { motion } from 'framer-motion';
import { Clock, Sparkles, Users, TrendingUp, Zap, Flame, Wallet, ExternalLink, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

// Mock crowdfund (replace with contract/WebSocket for real-time)
const CROWDFUND_GOAL_ETH = 20;
const CROWDFUND_RAISED_ETH = 8.42;

export default function HeroSection() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const target = new Date('2026-03-01T00:00:00Z');
    const t = setInterval(() => {
      const now = Date.now();
      const d = target.getTime() - now;
      if (d > 0)
        setTimeLeft({
          days: Math.floor(d / 864e5),
          hours: Math.floor((d % 864e5) / 36e5),
          minutes: Math.floor((d % 36e5) / 6e4),
          seconds: Math.floor((d % 6e4) / 1e3),
        });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const progressPct = Math.min(100, (CROWDFUND_RAISED_ETH / CROWDFUND_GOAL_ETH) * 100);

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 py-16 md:py-20">
      {/* Background: lighter for mobile */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center opacity-15 md:opacity-20"
        animate={{ scale: [1, 1.08, 1], rotate: [0, 3, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      >
        <div className="w-72 h-72 md:w-96 md:h-96 rounded-full border-4 border-purple-500 shadow-[0_0_60px_rgba(168,85,247,0.4)]" />
        <div className="absolute w-60 h-60 md:w-80 md:h-80 rounded-full border-4 border-blue-500 shadow-[0_0_50px_rgba(59,130,246,0.4)]" />
        <div className="absolute w-48 h-48 md:w-64 md:h-64 rounded-full border-4 border-pink-500 shadow-[0_0_40px_rgba(236,72,153,0.4)]" />
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto text-center">
        {/* Badge + Title */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 mb-4 md:mb-6 bg-purple-500/20 border border-purple-500/50 rounded-full text-purple-300 text-xs md:text-sm">
            <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
            <span>100% Fair Launch · No Presale · No VC</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-3 md:mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
            DejaPortal
          </h1>
          <p className="text-xl md:text-3xl text-gray-200 mb-1 md:mb-2 font-semibold">
            100% Fair Launch Time-Travel GameFi
          </p>
          <p className="text-base md:text-xl text-gray-400 mb-6 md:mb-8 max-w-2xl mx-auto">
            No presale, no VC. Back to 2016. Community-built portal.
          </p>
        </motion.div>

        {/* Current phase */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08 }}
          className="mb-6 md:mb-8"
        >
          <div className="inline-block bg-slate-800/70 backdrop-blur-sm border border-purple-500/30 rounded-xl px-4 py-3 md:px-6 md:py-4 text-left max-w-2xl mx-auto">
            <p className="text-sm md:text-base text-gray-300">
              <span className="font-semibold text-purple-300">Current phase:</span> Grants application + community crowdfund for LP. Target <span className="text-white font-semibold">$25–75k</span> initial liquidity.
            </p>
          </div>
        </motion.div>

        {/* Crowdfund progress: ETH / goal, % */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, delay: 0.12 }}
          className="mb-6 md:mb-8 max-w-md mx-auto"
        >
          <div className="bg-slate-800/60 backdrop-blur-sm border border-purple-500/40 rounded-xl p-4 md:p-5">
            <div className="flex items-center justify-between text-xs md:text-sm mb-1.5 md:mb-2">
              <span className="text-gray-400">Crowdfund progress</span>
              <span className="text-purple-300 font-medium">Live</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1.5 md:mb-2">
              <span className="text-xl md:text-2xl font-bold text-white">{CROWDFUND_RAISED_ETH} ETH</span>
              <span className="text-gray-400 text-sm md:text-base">/ {CROWDFUND_GOAL_ETH} ETH goal</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="text-right mt-1.5 text-sm text-gray-400">{progressPct.toFixed(1)}%</div>
          </div>
        </motion.div>

        {/* Fair Launch Countdown (real-time) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, delay: 0.16 }}
          className="mb-6 md:mb-8"
        >
          <div className="flex items-center justify-center gap-1.5 md:gap-2 mb-2 md:mb-3 text-purple-300">
            <Clock className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
            <span className="text-xs md:text-sm uppercase tracking-wider">Fair Launch Countdown</span>
          </div>
          <div className="flex justify-center gap-2 md:gap-3 flex-wrap">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Mins', value: timeLeft.minutes },
              { label: 'Secs', value: timeLeft.seconds },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-lg p-2.5 md:p-3 min-w-[56px] md:min-w-[70px]"
              >
                <div className="text-2xl md:text-3xl font-bold text-purple-400 tabular-nums">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{item.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Burn preview (placeholder) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mb-6 md:mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-200/90 text-sm">
            <Flame className="w-4 h-4 shrink-0" />
            <span>90%+ revenue burn after launch</span>
          </div>
        </motion.div>

        {/* CTA: 4 buttons */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.24 }}
          className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mb-10 md:mb-14"
        >
          <Link to="/crowdfund" className="inline-flex min-w-0">
            <motion.span
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-sm md:text-base shadow-lg shadow-purple-500/50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Contribute Crowdfund, Become OG
              <Sparkles className="w-4 h-4 shrink-0" />
            </motion.span>
          </Link>
          <a href="https://discord.gg/rRBSnQsxJu" target="_blank" rel="noopener noreferrer" className="inline-flex min-w-0">
            <motion.span
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 bg-slate-800/60 border-2 border-blue-500/50 text-blue-300 rounded-lg font-semibold text-sm md:text-base"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Join Discord, Grab OG Rewards
              <ExternalLink className="w-4 h-4 shrink-0" />
            </motion.span>
          </a>
          <Link to="/whitepaper" className="inline-flex min-w-0">
            <motion.span
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 bg-slate-800/60 border-2 border-purple-500/50 text-purple-300 rounded-lg font-semibold text-sm md:text-base"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View Whitepaper V2
              <FileText className="w-4 h-4 shrink-0" />
            </motion.span>
          </Link>
          <Link to="/mint" className="inline-flex min-w-0">
            <motion.span
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 bg-slate-800/60 border-2 border-pink-500/50 text-pink-300 rounded-lg font-semibold text-sm md:text-base"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Connect Wallet, Preview Portal
              <Wallet className="w-4 h-4 shrink-0" />
            </motion.span>
          </Link>
        </motion.div>

        {/* Key stats: mobile 2x2 grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto"
        >
          {[
            { icon: Users, label: 'Initial Liquidity', value: '$25–75k', sub: 'Grants + Crowdfund' },
            { icon: TrendingUp, label: 'Farming APY', value: '1000%', sub: 'First Month' },
            { icon: Zap, label: 'Portal NFT', value: '100,000', sub: 'Limited Supply' },
            { icon: Flame, label: 'Burn', value: '90%+', sub: 'After Launch' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-slate-800/30 backdrop-blur-sm border border-purple-500/20 rounded-lg p-4 md:p-5"
            >
              <stat.icon className="w-6 h-6 md:w-7 md:h-7 text-purple-400 mx-auto mb-1.5 md:mb-2" />
              <div className="text-lg md:text-2xl font-bold text-white mb-0.5">{stat.value}</div>
              <div className="text-xs md:text-sm text-gray-400">{stat.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.sub}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
